//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IScope } from 'angular';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyErrorText } from '@components/atoms/text/Text';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from '@components/molecules/pagination/paginationConstants';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import IItemMetadata, {
  Product,
  UiaProductTagRequest,
} from '@models/IItemMetadata';
import makeAngularReactControllerWithScope, {
  applyScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { getIsShareV2Vendor } from '@redux/selectors/getSessionInfo';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingContextProvider } from '@tracker/mediaTracking/contexts/MediaTrackingContextProvider';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { getRootCuid } from '@uia/details/selectors/getUIAReactPortItemDetails';
import { UIAContainer } from '@uia/layout/UIAContainer';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { ProductsContainer } from '@uia/products/ProductsContainer';
import {
  SEARCH_DEBOUNCE_DELAY,
  TagLocation,
} from '@uia/products/constants/UiaProductConstants';
import { ProductTaggingContext } from '@uia/products/context/ProductTaggingContext';
import { useCurrentTrackingParamsQuery } from '@uia/products/hooks/useCurrentTrackingParamsQuery';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import {
  MutationVariables,
  useProductTagsMutation,
} from '@uia/products/hooks/useProductTagsMutation';
import { useProductsQuery } from '@uia/products/hooks/useProductsQuery';
import { getIsUrlTrackingParamsEnabled } from '@uia/products/selectors/getIsUrlTrackingParamsEnabled';
import { showDefaultErrorDialog } from '@utils/errorNotifications';
import { isDefined, isNotNull, isUndefined } from '@utils/filterPredicates';

export interface UIAProductsProps {
  addingProductBySpatialTag: boolean;
  boxId: string;
  broadcast: (name: string, item?: object) => void;
  getCatalogSuggestions: () => CuralateCatalogProduct[];
  handleAllProductsRemoved: () => void;
  handleProductRemoved: (productStyleId: string) => void;
  handleSaveError: (error: unknown) => void;
  handleTagProduct: (productsList: Product[], tagLocation: TagLocation) => void;
  handleUpdateProduct: (
    updatedProduct: UiaProductTagRequest,
    productsList: Product[],
  ) => void;
  isProductEditingFeatureDisabled: boolean;
  onCloseModal: () => void;
  productSuggestionBoxId: string;
  resetAddEditFlow: () => void;
  shouldShowCatalogSelector: boolean;
  spatialTagOffsets: { offsetX: number; offsetY: number };
  trackProductTaggingEvent: (product: UiaProductTagRequest) => void;
}

interface UniversalImageActionsProductsProps extends UIAProductsProps {
  cuid: Cuid;
  metadata: IItemMetadata;
}

const UniversalImageActionsProducts = ({
  addingProductBySpatialTag,
  boxId,
  broadcast,
  cuid,
  getCatalogSuggestions,
  handleAllProductsRemoved,
  handleProductRemoved,
  handleSaveError,
  handleTagProduct,
  handleUpdateProduct,
  isProductEditingFeatureDisabled,
  metadata,
  onCloseModal,
  productSuggestionBoxId,
  resetAddEditFlow,
  shouldShowCatalogSelector,
  spatialTagOffsets,
  trackProductTaggingEvent,
}: UniversalImageActionsProductsProps) => {
  const isShareV2Vendor = useSelector(getIsShareV2Vendor);
  const isTrackingParamsEnabled = useSelector(getIsUrlTrackingParamsEnabled);
  const { source, trackEvent: mediaTrackingEvent } = useMediaTrackingContext();
  const { data: trackingParamsVersion } = useCurrentTrackingParamsQuery();

  const onMutationSuccess = (data: Product[], variables: MutationVariables) => {
    switch (variables.mutationType) {
      case 'add':
        handleTagProduct(data, variables.tagLocation);
        break;
      case 'edit':
        handleUpdateProduct(variables.updatedProduct, data);
        break;
      case 'remove':
        handleProductRemoved(variables.productStyleId);
        break;
      case 'removeAll':
        handleAllProductsRemoved();
        break;
      case 'reorder':
        break;
    }
  };

  const {
    isPending: isProcessingProductTagsMutation,
    mutate,
    variables,
  } = useProductTagsMutation({
    broadcast,
    cuid,
    onError: handleSaveError,
    onSuccess: onMutationSuccess,
    trackProductTaggingEvent,
  });

  const addProduct = (
    newProduct: UiaProductTagRequest,
    productTags: Product[],
  ) => {
    mediaTrackingEvent({
      cuid: cuid,
      event: MediaTrackingEventName.ProductAdded,
    });

    const isProductAlreadyAdded = productTags.some(
      (item) => item.productStyleId === newProduct.productStyleId,
    );

    if (isProductAlreadyAdded) {
      return showDefaultErrorDialog(
        'This product is already tagged in this image.',
      );
    }

    resetAddEditFlow();

    if (addingProductBySpatialTag) {
      const searchResultsProductPosition = productsData?.products.findIndex(
        (product) => product.productStyleId === newProduct.productStyleId,
      );

      mutate({
        boxId,
        isCatalogSuggestions: productsData?.isCatalogSuggestions,
        mutationType: 'add',
        newProducts: [
          {
            ...newProduct,
            spatialTag: {
              label: newProduct.position.toString(),
              x: spatialTagOffsets.offsetX,
              y: spatialTagOffsets.offsetY,
            },
          },
        ],
        productList: productTags,
        searchResultsProductPosition,
        tagLocation: TagLocation.SPATIAL,
      });
    } else {
      mutate({
        isCatalogSuggestions: productsData?.isCatalogSuggestions,
        mutationType: 'add',
        newProducts: [newProduct],
        productList: productTags,
        tagLocation: TagLocation.NONE,
      });
    }
  };

  const bulkTagProducts = (
    selectedProducts: CuralateCatalogProduct[],
    productTags: Product[],
  ) => {
    const isProductAlreadyAdded = (newProduct: CuralateCatalogProduct) =>
      productTags.some(
        (item) => item.productStyleId === newProduct.productStyleId,
      );

    const validProductsToAdd = selectedProducts.filter(
      (product) =>
        isDefined(product.destinationUrl) && !isProductAlreadyAdded(product),
    );

    const trackingSettings = {
      trackingParamsVersion:
        isTrackingParamsEnabled &&
        isDefined(trackingParamsVersion) &&
        isNotNull(trackingParamsVersion)
          ? trackingParamsVersion
          : undefined,
    };

    const startPosition = productTags.length + 1;

    const uiaProductsToTag: UiaProductTagRequest[] = validProductsToAdd.map(
      (product, idx) => ({
        ...product,
        ...(product.images[0] ?? {}),
        ...trackingSettings,
        position: startPosition + idx,
      }),
    );

    if (isDefined(cuid)) {
      mediaTrackingEvent({
        cuid,
        event: MediaTrackingEventName.ProductAdded,
      });
    }

    resetAddEditFlow();

    if (addingProductBySpatialTag) {
      const productsWithSpatialTags = uiaProductsToTag.map((product) => ({
        ...product,
        spatialTag: {
          label: product.position.toString(),
          x: spatialTagOffsets.offsetX,
          y: spatialTagOffsets.offsetY,
        },
      }));

      // In bulk tagging, we always send the position of the 1st product in the list.
      // The mutation's linkProductToBoundingBoxV2 API call only fires when newProducts.length === 1,
      // so for bulk operations with multiple products, this position is not used.
      const searchResultsProductPosition = productsData?.products.findIndex(
        (product) =>
          product.productStyleId === productsWithSpatialTags[0]?.productStyleId,
      );

      mutate({
        boxId,
        isCatalogSuggestions: productsData?.isCatalogSuggestions,
        mutationType: 'add',
        newProducts: productsWithSpatialTags,
        productList: productTags,
        searchResultsProductPosition,
        tagLocation: TagLocation.SPATIAL,
      });
    } else {
      mutate({
        isCatalogSuggestions: productsData?.isCatalogSuggestions,
        mutationType: 'add',
        newProducts: uiaProductsToTag,
        productList: productTags,
        tagLocation: TagLocation.NONE,
      });
    }
  };

  const cancelNewSpatialTag = () => {
    if (addingProductBySpatialTag) {
      broadcast('uia:products:cancelNewSpatialTag');
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState<number>(DEFAULT_PAGE_NUMBER);
  const [catalogClientId, setCatalogClientId] = useState<ClientId | undefined>(
    undefined,
  );
  const [isTaggingProducts, setIsTaggingProducts] = useState(false);
  const [selectedProductToEdit, setSelectedProductToEdit] = useState<
    Product | CuralateCatalogProduct | undefined
  >();

  const { data: catalogs } = useProductCatalogsQuery();

  const hasCatalogConnected = isDefined(catalogs) && catalogs.length > 0;

  const [debouncedSearchQuery] = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY,
  );

  useEffect(() => {
    setIsTaggingProducts(addingProductBySpatialTag);
  }, [addingProductBySpatialTag]);

  const { data: productsData, isPending: isLoadingProducts } = useProductsQuery(
    {
      catalogClientId,
      getCatalogSuggestions,
      hasCatalog: hasCatalogConnected,
      isBvCampaignTagged: metadata.isBvCampaignTagged,
      isEnabled: isDefined(cuid),
      isShareV2Vendor,
      itemId: Cuid.toPartial(cuid),
      limit,
      offset,
      productSuggestionBoxId,
      search: debouncedSearchQuery,
    },
  );

  const handlePageChange = (currentPage: number, itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(currentPage * itemsPerPage);
  };

  const contextValue = {
    addProduct,
    bulkTagProducts,
    cancelNewSpatialTag,
    catalogClientId,
    cuid,
    editProduct: ({ productList, updatedProduct }) =>
      mutate({ mutationType: 'edit', updatedProduct, productList }),
    handlePageChange,
    hasCatalogConnected,
    isCatalogSuggestions: productsData?.isCatalogSuggestions,
    isLoadingProducts,
    isProductEditingFeatureDisabled,
    isProcessingProductTagsMutation,
    isAddingOrRemovingProduct:
      isProcessingProductTagsMutation &&
      (variables?.mutationType === 'add' ||
        variables?.mutationType === 'remove' ||
        variables?.mutationType === 'removeAll'),
    isTaggingProducts,
    limit,
    metadata,
    offset,
    products: productsData?.products,
    removeAllProducts: () =>
      mutate({ mutationType: 'removeAll', productList: [] }),
    removeProduct: ({ listOfProducts, productStyleId }) =>
      mutate({
        mutationType: 'remove',
        productStyleId,
        productList: listOfProducts,
      }),
    reorderProducts: ({ reorderedProducts }) =>
      mutate({ mutationType: 'reorder', productList: reorderedProducts }),
    searchQuery,
    selectedProductToEdit,
    setCatalogClientId,
    setIsTaggingProducts,
    setLimit,
    setOffset,
    setSearchQuery,
    setSelectedProductToEdit,
    shouldShowCatalogSelector,
    totalCount: productsData?.totalCount ?? 0,
  } satisfies ProductTaggingContext;

  return (
    <StyledUIAContainer>
      <ProductTaggingContext.Provider value={contextValue}>
        <MediaTrackingContextProvider source={source}>
          <ProductsContainer />
          {!isTaggingProducts && isUndefined(selectedProductToEdit) && (
            <StyledUIAFooter>
              <UIAFooter.Actions>
                <UIAFooter.ActionButton
                  onClick={onCloseModal}
                  trackingId={UIATrackingIds.PRODUCTS_CLOSE_MODAL}
                >
                  Done
                </UIAFooter.ActionButton>
              </UIAFooter.Actions>
            </StyledUIAFooter>
          )}
        </MediaTrackingContextProvider>
      </ProductTaggingContext.Provider>
    </StyledUIAContainer>
  );
};

const UIAProducts = (props: UIAProductsProps) => {
  const cuid = useSelector(getRootCuid);
  const { data: metadata, isError, isPending } = useMediaMetadataQuery(cuid);

  if (isPending) {
    return <CenteredSpinner />;
  }

  if (isError || !cuid) {
    return (
      <BodyErrorText>
        An unexpected error occurred. Please try again or contact Bazaarvoice
        Support.
      </BodyErrorText>
    );
  }

  return (
    <UniversalImageActionsProducts {...props} cuid={cuid} metadata={metadata} />
  );
};

const StyledUIAContainer = styled(UIAContainer)`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledUIAFooter = styled(UIAFooter)`
  margin-top: auto;
`;

angular.module('curalate').component('uiaProducts', {
  bindings: {
    addingProductBySpatialTag: '<',
    boxId: '<',
    broadcast: '&',
    getCatalogSuggestions: '&',
    handleAllProductsRemoved: '&',
    handleProductRemoved: '&',
    handleSaveError: '&',
    handleTagProduct: '&',
    handleUpdateProduct: '&',
    isProductEditingFeatureDisabled: '<',
    productSuggestionBoxId: '<',
    resetAddEditFlow: '&',
    shouldShowCatalogSelector: '<',
    spatialTagOffsets: '<',
    trackProductTaggingEvent: '&',
  },
  controller: makeAngularReactControllerWithScope(
    (
      scope: {
        $ctrl: UIAProductsProps;
      } & IScope,
    ) => {
      return (
        <UIAProducts
          addingProductBySpatialTag={scope.$ctrl.addingProductBySpatialTag}
          boxId={scope.$ctrl.boxId}
          broadcast={(name, item) =>
            applyScope(scope, scope.$ctrl.broadcast)({ name, item })
          }
          getCatalogSuggestions={() =>
            applyScope(scope, scope.$ctrl.getCatalogSuggestions)()
          }
          handleAllProductsRemoved={() =>
            applyScope(scope, scope.$ctrl.handleAllProductsRemoved)()
          }
          handleProductRemoved={(productStyleId) =>
            applyScope(
              scope,
              scope.$ctrl.handleProductRemoved,
            )({ productStyleId })
          }
          handleSaveError={(error) =>
            applyScope(scope, scope.$ctrl.handleSaveError)({ error })
          }
          handleTagProduct={(productsList, tagLocation) =>
            applyScope(
              scope,
              scope.$ctrl.handleTagProduct,
            )({ productsList, tagLocation })
          }
          handleUpdateProduct={(updatedProduct, productsList) =>
            applyScope(
              scope,
              scope.$ctrl.handleUpdateProduct,
            )({ updatedProduct, productsList })
          }
          isProductEditingFeatureDisabled={
            scope.$ctrl.isProductEditingFeatureDisabled
          }
          onCloseModal={() => scope.$emit('uia:modal:close')}
          productSuggestionBoxId={scope.$ctrl.productSuggestionBoxId}
          resetAddEditFlow={() =>
            applyScope(scope, scope.$ctrl.resetAddEditFlow)()
          }
          shouldShowCatalogSelector={scope.$ctrl.shouldShowCatalogSelector}
          spatialTagOffsets={scope.$ctrl.spatialTagOffsets}
          trackProductTaggingEvent={(product) =>
            applyScope(scope, scope.$ctrl.trackProductTaggingEvent)({ product })
          }
        />
      );
    },
  ),
});
